/**
 * src/services/settings.api.ts 의 짝 — 회사 설정과 내 설정, 그리고 내 알림.
 *
 * 설정은 사무소 하나당 한 행이라 PATCH 가 그 행을 고친다. 내 알림 설정은 행이 없는 종류를 켜진
 * 것으로 본다 — 새 종류를 넣었을 때 알림이 조용히 줄지 않게 하려는 것이다.
 */
import { HttpResponse, http } from "msw";
import type { MySettings, MySettingsPatch, NotificationKind, SiteConfigPatch } from "@/types";
import type { NotificationSubject } from "../data/notifications";
import { db } from "../db";
import { fail, latency, requireAdmin, requireUser } from "../http";

const BASE = "/api/v1";

const ALL_KINDS: NotificationKind[] = [
  "daily_missing",
  "daily_comment",
  "daily_edit_requested",
  "daily_edit_decided",
  "vacation_requested",
  "vacation_decided",
  "vacation_cancel_requested",
  "vacation_cancel_decided",
];

/** 개인이 끌 수 있는 종류 — backend `USER_MUTABLE_KINDS`. 결재 요청과 내 신청의 결과는 끄지 못한다. */
const MUTABLE: NotificationKind[] = ["daily_missing", "daily_comment"];

/**
 * 결재 요청 알림이 아직 처리되지 않았는가 — 가리키는 신청의 지금 상태로 센다. 한 알림이 여러 건을 묶으면
 * 하나라도 남아 있으면 처리 전이다. 결재 요청이 아닌 알림(subject 없음)은 false.
 */
const pendingOf = (subject: NotificationSubject | undefined): boolean => {
  if (!subject) return false;
  return subject.ids.some((id) => {
    if (subject.type === "daily_edit")
      return db.dailyEditRequests.some((r) => r.id === id && r.status === "pending");
    if (subject.type === "schedule")
      return db.schedules.some((s) => s.id === id && s.status === "pending" && !s.cancelled_at);
    const vacation = db.vacations.find((v) => v.id === id);
    if (!vacation) return false;
    return subject.type === "vacation"
      ? vacation.status === "pending"
      : vacation.status === "approved" && !!vacation.cancel_requested_at;
  });
};

/** 내 설정 — 행이 없으면 켜진 것으로 본다. 끌 수 없는 종류는 늘 켜져 있다. */
const mySettingsOf = (userId: string): MySettings => ({
  notifications: ALL_KINDS.map((kind) => {
    const can_disable = MUTABLE.includes(kind);
    const mine = db.userNotificationSettings.find(
      (row) => row.user_id === userId && row.kind === kind
    );
    return {
      kind,
      is_enabled: !can_disable || (mine?.is_enabled ?? true),
      can_disable,
    };
  }),
});

export const settingsHandlers = [
  http.get(`${BASE}/admin/settings/`, async () => {
    await latency();
    const admin = requireAdmin();
    return admin instanceof Response ? admin : HttpResponse.json(db.siteConfig);
  }),

  http.patch(`${BASE}/admin/settings/`, async ({ request }) => {
    await latency();
    const admin = requireAdmin();
    if (admin instanceof Response) return admin;
    const patch = (await request.json()) as SiteConfigPatch;
    const next = { ...db.siteConfig, ...patch };
    if (next.work_end <= next.work_start) return fail(422, "근무 끝이 시작보다 빨라요.");
    if (next.lunch_end <= next.lunch_start) return fail(422, "점심 끝이 시작보다 빨라요.");
    db.siteConfig = { ...next, updated_at: new Date().toISOString() };
    return HttpResponse.json(db.siteConfig);
  }),

  http.get(`${BASE}/settings/me/`, async () => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    return HttpResponse.json(mySettingsOf(user.id));
  }),

  http.patch(`${BASE}/settings/me/`, async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const { notifications = [] } = (await request.json()) as MySettingsPatch;
    // 끌 수 없는 종류를 끄려 하면 아무것도 저장하지 않는다 — 서버가 422 로 막는 자리다.
    const required = notifications.filter((row) => !row.is_enabled && !MUTABLE.includes(row.kind));
    if (required.length)
      return fail(
        422,
        `꼭 받아야 하는 알림은 끌 수 없습니다: ${required.map((row) => row.kind).join(", ")}.`
      );
    for (const { kind, is_enabled } of notifications) {
      const found = db.userNotificationSettings.find(
        (row) => row.user_id === user.id && row.kind === kind
      );
      if (found) found.is_enabled = is_enabled;
      else db.userNotificationSettings.push({ user_id: user.id, kind, is_enabled });
    }
    return HttpResponse.json(mySettingsOf(user.id));
  }),

  http.get(`${BASE}/notifications/`, async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const unread = new URL(request.url).searchParams.get("unread") === "true";
    const results = db.notifications
      .filter((row) => row.user_id === user.id)
      .filter((row) => !unread || !row.read_at)
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
      .map(({ user_id: _userId, subject, ...row }) => ({
        ...row,
        pending: pendingOf(subject),
        // 만든 사람의 사진은 지금 값으로 — 관리자가 사진을 바꾸면 알림에도 바로 보인다.
        actor: row.actor && {
          ...row.actor,
          avatar_url: db.users.find((u) => u.id === row.actor?.id)?.avatar_url ?? null,
        },
      }));
    return HttpResponse.json({ results });
  }),

  http.post(`${BASE}/notifications/read-all/`, async () => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const now = new Date().toISOString();
    let updated = 0;
    for (const row of db.notifications)
      if (row.user_id === user.id && !row.read_at) {
        row.read_at = now;
        updated += 1;
      }
    return HttpResponse.json({ updated });
  }),

  http.post(`${BASE}/notifications/:id/read/`, async ({ params }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const found = db.notifications.find((row) => row.id === params.id && row.user_id === user.id);
    if (!found) return fail(404, "없는 알림입니다.");
    found.read_at ??= new Date().toISOString();
    const { user_id: _userId, subject, ...row } = found;
    return HttpResponse.json({ ...row, pending: pendingOf(subject) });
  }),
];
